import { useEffect, useRef, useState } from "react";
import { useAppContext } from "../../../libs/app-core/AppContext";
import { MessageDialog } from "../../../libs/app-core/dialogs/MessageDialog";
import { UniAnswer } from "../../dao/UniAnswer";
import { UniAnswerDAO } from "../../dao/UniAnswerDAO";
import { UniForm } from "../../dao/UniForm";
import { UniFormDAO } from "../../dao/UniFormDAO";
import { UniInputDAO } from "../../dao/UniInputDAO";
import { UniOptionDAO } from "../../dao/UniOptionDAO";
import { UniQuestionDAO } from "../../dao/UniQuestionDAO";


/**
 * 輸入: 
 * uni_answer_guid 指定 answer
 * 
 * 輸出:
 * _UniAnswer 問卷答案(匯入時會新增空白的答案紙，裡面會標記應該使用哪個 特作共用題目 與 該做的問卷列表) 
 * _SpwkBaseForm 所有問卷共用的題目 1.基本資料、2.作業經歷、5.生活習慣 ...
 * _UniFormList 所有問卷列表(已填好樹狀圖 UniForm->UniQuestion->UniOption->UniInput)
 * 
 * - 目前綁定的項目，會重新安排 Parent
 */
export function useCommonQuest(uni_answer_guid: string | undefined) {

  const ctx = useAppContext();

  /* 回傳: 答案卷 Answer (內涵要做哪些特作) */
  const [_UniAnswer, set_UniAnswer] = useState<UniAnswer | null | undefined>(undefined);
  const _UniAnswerRef = useRef(_UniAnswer);

  /* 回傳: 一般問卷 */
  const [_CommonForm, set_CommonForm] = useState<UniForm | null | undefined>(undefined);
  const _CommonFormRef = useRef(_CommonForm);

  async function async_func() {

    try {
      //#region 確認 uni_answer_guid 以準備好 or return;
      if (uni_answer_guid === undefined) {
        /* 變數尚未準備好 */
        set_UniAnswer(undefined);
        _UniAnswerRef.current = undefined;

        set_CommonForm(undefined);
        _CommonFormRef.current = undefined;

        return;
      }
      //#endregion

      //#region 取得 Answer
      const answer = await UniAnswerDAO.WS_FetchByGuid(uni_answer_guid);

      if (answer == null) {
        /* 查無 Answer */
        set_UniAnswer(null);
        _UniAnswerRef.current = null;

        set_CommonForm(undefined);
        _CommonFormRef.current = undefined;
        return;
      }

      set_UniAnswer(answer);
      _UniAnswerRef.current = answer;
      //#endregion

      const common_form_guid = answer.form_guid;// 問卷通用問題版本

      /* 先混合一起建立樹狀圖 */
      let form_guid_list = [common_form_guid];

      /* 為了減少 Select 次數，將所有相關的資料一次撈出來，之後再用 .filter() 重新分配 */
      const cache_forms = await UniFormDAO.WS_SelectForms_ByFormGuidList(form_guid_list);
      const cache_questions = await UniQuestionDAO.WS_SelectQuestions_ByFormGuidList(form_guid_list);
      const cache_options = await UniOptionDAO.WS_SelectOptions_ByFormGuidList(form_guid_list);
      const cache_inputs = await UniInputDAO.DB_SelectInputs_ByFormGuidList(form_guid_list);

      /* 填好樹狀結構 $$$ 核心方法 */
      UniFormDAO.FillFormTree(cache_forms, cache_questions, cache_options, cache_inputs);


      /* 找出特作通用問題 */
      const find_common_form_guid = cache_forms.find(x => x.guid === common_form_guid) ?? null;

      /* 設定特作基礎項 */
      set_CommonForm(find_common_form_guid);
      _CommonFormRef.current = find_common_form_guid;
    } catch (ex: any) {
      MessageDialog.Show(ctx, ex.message, "ApiError!", { colorMeaning: 'Danger' });
      ctx.NavigateToErrorPage("", "useSpwkQuest", ex.message);
    }

  };

  useEffect(() => {
    async_func();

  }, [uni_answer_guid]);


  return {
    /** 問卷 Answer (內涵 通用問題版本 與 特殊作業問卷版本列表) */
    _UniAnswer,

    /** 此問卷通用問題(指定某版本) */
    _CommonForm,

    _UniAnswerRef,

    _CommonFormRef,
  };



}



/**
 * 將各題目依照 v3_group 另外重新分組
 * 主要
 * - 將A01等原本合在一起的選項，拆分成個別系統
 * - 將其他的選項也歸屬到個別系統
 **/

/**
 * 對所有 Form 的 既往病史 或 自覺症狀 中的選項
 * 一一提出來重新加入 BaseForm 的固定通用題目分組中 (血液系統、呼吸系統...)
 * 
 */


/**
 * 嘗試從 分類列表 找到此 選項 的歸屬
 * 如果找不到，則回傳 group_not_found (歸屬在找不到!)
 */

/**
 * - 重新排序 [無、一般項目、其他]
 * - 隱藏拆分前的父項目
 * - 排除重複的疾病
 * - 補上 無
 */


/**
 * □慢性氣管炎、肺氣腫 已被拆分為 □慢性氣管炎 □肺氣腫  
 * 原本的項目就不用再顯示
 */


